import { useInjection } from 'inversify-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v7 as uuidv7 } from 'uuid';
import { useNotification } from '../../../hooks/use-notification';
import {
  type PreviewErrorEvent,
  type PreviewReadyEvent,
  usePreviewSse,
} from '../../../hooks/use-preview-sse';
import {
  GoodsReceivingService,
  type IGoodsReceivingService,
} from '../../../services/goods-receiving';
import { isPdfFile } from '../../../utils/pdf/detector';
import { processPhoto, requiresConversion, revokePreviewUrl } from '../utils/photo-processor';

export interface PhotoItem {
  id: string;
  file: File;
  previewUrls: string[];
  isConverting: boolean;
  sourceKey?: string;
  /** True once the file has been confirmed uploaded to backend via syncPhotoSourceKeys.
   * Excludes the item from getReadyFiles so SSE resolution does not re-trigger onChange. */
  isSynced?: boolean;
}

interface UsePhotoManagerOptions {
  maxFiles: number;
  onError?: (message: string) => void;
  /** Called when an SSE preview-ready event resolves for a PDF item. */
  onPreviewReady?: () => void;
}

interface UsePhotoManagerReturn {
  photoItems: PhotoItem[];
  isProcessing: boolean;
  readyCount: number;
  addPhotos: (files: File[]) => Promise<{ id: string; file: File }[]>;
  removePhoto: (id: string) => void;
  syncFromExternal: (files: File[]) => void;
  getReadyFiles: () => File[];
  syncPhotoSourceKeys: (
    backendPhotos: { id: string; sourceKey?: string; originalFilename: string }[],
  ) => void;
}

async function processSingleFile(
  file: File,
  id: string,
  setPhotoItems: React.Dispatch<React.SetStateAction<PhotoItem[]>>,
  onError?: (message: string) => void,
): Promise<PhotoItem | null> {
  if (requiresConversion(file)) {
    setPhotoItems((prev) => [...prev, { id, file, previewUrls: [], isConverting: true }]);
  }

  const result = await processPhoto(file);

  if (result.success) {
    return {
      id,
      file: result.photo.file,
      previewUrls: [result.photo.previewUrl],
      isConverting: false,
    };
  }

  onError?.(result.error.message);
  setPhotoItems((prev) => prev.filter((p) => p.id !== id));
  return null;
}

/**
 * Process a single file and return the PhotoItem if successful.
 * Returns null if processing failed or file should be skipped.
 */
async function processFile(
  file: File,
  setPhotoItems: React.Dispatch<React.SetStateAction<PhotoItem[]>>,
  onError?: (message: string) => void,
): Promise<{ id: string; item: PhotoItem } | null> {
  const id = uuidv7();

  // PDF: add as-is with no preview — backend converts to JPEG on upload
  if (isPdfFile(file)) {
    return {
      id,
      item: { id, file, previewUrls: [], isConverting: false },
    };
  }

  const item = await processSingleFile(file, id, setPhotoItems, onError);
  return item ? { id, item } : null;
}

/**
 * Hook per gestire lo stato delle foto con conversione HEIC e preview
 */
export function usePhotoManager(options: UsePhotoManagerOptions): UsePhotoManagerReturn {
  const { maxFiles, onError, onPreviewReady } = options;
  const { t } = useTranslation();
  const { showError } = useNotification();
  const goodsReceivingService = useInjection<IGoodsReceivingService>(GoodsReceivingService);

  const [photoItems, setPhotoItems] = useState<PhotoItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // Tracks how many SSE preview events are still expected. More reliable than
  // checking isConverting (which can briefly be false between two PDF uploads).
  const [pendingSourceCount, setPendingSourceCount] = useState(0);

  const photoItemsRef = useRef<PhotoItem[]>([]);
  photoItemsRef.current = photoItems;

  // Map to track sourceKey -> photo item id for matching SSE events
  const pendingSourceKeysRef = useRef<Map<string, string>>(new Map());

  // Map of filename -> photoId for recently added photos (before backend sync)
  const recentPhotoMappingsRef = useRef<Map<string, string>>(new Map());

  const onPreviewReadyRef = useRef(onPreviewReady);
  onPreviewReadyRef.current = onPreviewReady;

  // Subscribe to SSE for preview updates (PDFs converted server-side)
  usePreviewSse({
    enabled: pendingSourceCount > 0,
    onPreviewReady: useCallback(
      (event: PreviewReadyEvent) => {
        const photoId = pendingSourceKeysRef.current.get(event.result.sourceKey);
        if (!photoId) {
          return;
        }
        pendingSourceKeysRef.current.delete(event.result.sourceKey);
        setPendingSourceCount((prev) => Math.max(0, prev - 1));

        const storageKeys = event.result.pages.map((p) => p.storageKey);
        if (storageKeys.length === 0) {
          setPhotoItems((prev) =>
            prev.map((p) => (p.id === photoId ? { ...p, isConverting: false } : p)),
          );
          onPreviewReadyRef.current?.();
          return;
        }

        // Resolve all page storage keys to presigned URLs the browser can fetch.
        Promise.all(storageKeys.map((key) => goodsReceivingService.getPresignedUrl(key)))
          .then((presignedUrls) => {
            setPhotoItems((prev) =>
              prev.map((p) =>
                p.id === photoId ? { ...p, previewUrls: presignedUrls, isConverting: false } : p,
              ),
            );
            onPreviewReadyRef.current?.();
          })
          .catch(() => {
            setPhotoItems((prev) =>
              prev.map((p) => (p.id === photoId ? { ...p, isConverting: false } : p)),
            );
          });
      },
      [goodsReceivingService],
    ),
    onPreviewError: useCallback(
      (_errorEvent: PreviewErrorEvent) => {
        showError(t('errors.previewError'));
        const clearedCount = pendingSourceKeysRef.current.size;
        pendingSourceKeysRef.current.clear();
        setPendingSourceCount((prev) => Math.max(0, prev - clearedCount));
        setPhotoItems((prev) =>
          prev.map((p) => (p.isConverting ? { ...p, isConverting: false } : p)),
        );
      },
      [showError, t],
    ),
  });

  // Cleanup dei blob URL quando il componente viene smontato
  useEffect(() => {
    return () => {
      for (const item of photoItemsRef.current) {
        for (const url of item.previewUrls) {
          revokePreviewUrl(url);
        }
      }
    };
  }, []);

  const addPhotos = useCallback(
    async (files: File[]) => {
      const currentCount = photoItemsRef.current.filter((p) => !p.isConverting).length;
      const availableSlots = maxFiles - currentCount;

      if (availableSlots <= 0) {
        onError?.(t('common.fileErrors.maxFiles', { max: maxFiles }));
        return [];
      }

      const filesToProcess = files.slice(0, availableSlots);
      setIsProcessing(true);

      try {
        const newItems: PhotoItem[] = [];
        const addedMappings: { id: string; file: File }[] = [];

        for (const file of filesToProcess) {
          const result = await processFile(file, setPhotoItems, onError);
          if (result) {
            newItems.push(result.item);
            addedMappings.push({ id: result.id, file });
          }
        }

        if (newItems.length > 0) {
          setPhotoItems((prev) => {
            const currentItems = prev.filter((p) => !p.isConverting);
            return [...currentItems, ...newItems];
          });

          // Store mappings for sync with backend response
          for (const { id, file } of addedMappings) {
            recentPhotoMappingsRef.current.set(file.name, id);
          }
        }

        return addedMappings;
      } finally {
        setIsProcessing(false);
      }
    },
    [maxFiles, onError, t],
  );

  const removePhoto = useCallback((id: string) => {
    setPhotoItems((prev) => {
      const item = prev.find((p) => p.id === id);
      for (const url of item?.previewUrls ?? []) {
        revokePreviewUrl(url);
      }
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const syncFromExternal = useCallback((files: File[]) => {
    setPhotoItems((prev) => {
      // Cleanup blob URLs esistenti
      for (const item of prev) {
        for (const url of item.previewUrls) {
          revokePreviewUrl(url);
        }
      }

      if (files.length === 0) {
        return [];
      }

      return files.map((file) => ({
        id: uuidv7(),
        file,
        previewUrls: isPdfFile(file) ? [] : [URL.createObjectURL(file)],
        isConverting: false,
      }));
    });
  }, []);

  const getReadyFiles = useCallback(() => {
    return photoItems.filter((p) => !p.isConverting && !p.isSynced).map((p) => p.file);
  }, [photoItems]);

  const syncPhotoSourceKeys = useCallback(
    (backendPhotos: { id: string; sourceKey?: string; originalFilename: string }[]) => {
      if (backendPhotos.length === 0) {
        return;
      }

      // Pre-compute how many new SSE events to expect using the latest snapshot.
      const newPendingCount = backendPhotos.filter(
        (bp) =>
          !!bp.sourceKey &&
          photoItemsRef.current.some(
            (p) => isPdfFile(p.file) && p.file.name === bp.originalFilename,
          ),
      ).length;

      setPhotoItems((prev) => {
        const updated = prev.map((photo) => {
          if (!isPdfFile(photo.file)) {
            return photo;
          }

          const backendPhoto = backendPhotos.find((bp) => bp.originalFilename === photo.file.name);
          if (backendPhoto?.sourceKey) {
            pendingSourceKeysRef.current.set(backendPhoto.sourceKey, photo.id);
            return { ...photo, isConverting: true, isSynced: true };
          }
          return photo;
        });
        return updated;
      });

      if (newPendingCount > 0) {
        setPendingSourceCount((prev) => prev + newPendingCount);
      }
    },
    [],
  );

  const readyCount = photoItems.filter((p) => !p.isConverting && !p.isSynced).length;

  return {
    photoItems,
    isProcessing,
    readyCount,
    addPhotos,
    removePhoto,
    syncFromExternal,
    getReadyFiles,
    syncPhotoSourceKeys,
  };
}
